import styled, { keyframes } from 'styled-components'
import { Link } from 'react-router-dom'

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const checkAnimation = keyframes`
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
`

export const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 80px 20px;
  min-height: 80vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.6s ease-out;
`

export const SuccessIcon = styled.div`
  width: 120px;
  height: 120px;
  background: ${({ theme }) => theme.colors.success};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 32px;
  animation: ${checkAnimation} 0.5s ease-out 0.2s both;
  box-shadow: 0 8px 32px rgba(34, 197, 94, 0.3);

  svg {
    color: white;
  }
`

export const Title = styled.h1`
  font-size: 2.5rem;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 16px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`

export const Message = styled.p`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  margin-bottom: 40px;
  max-width: 600px;
  line-height: 1.6;
`

export const InfoCard = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 32px;
  width: 100%;
  max-width: 500px;
`

export const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-bottom: none;
  }

  .label {
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 0.95rem;
  }

  .value {
    color: ${({ theme }) => theme.colors.text};
    font-weight: 600;
    font-size: 1rem;
  }
`

export const ButtonsContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
  }
`

export const PrimaryButton = styled(Link)`
  display: inline-block;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  padding: 16px 40px;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  text-align: center;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryHover};
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(100, 100, 255, 0.3);
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`

export const SecondaryButton = styled(Link)`
  display: inline-block;
  background: ${({ theme }) => theme.colors.cardBackground};
  color: ${({ theme }) => theme.colors.text};
  border: 2px solid ${({ theme }) => theme.colors.border};
  padding: 16px 40px;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  text-align: center;

  &:hover {
    background: ${({ theme }) => theme.colors.hoverBackground};
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  animation: ${fadeIn} 0.6s ease-out;

  .spinner {
    width: 60px;
    height: 60px;
    border: 4px solid ${({ theme }) => theme.colors.border};
    border-top-color: ${({ theme }) => theme.colors.primary};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  p {
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 1.1rem;
  }
`

export const ErrorContainer = styled.div`
  text-align: center;
  animation: ${fadeIn} 0.6s ease-out;

  .error-icon {
    width: 100px;
    height: 100px;
    background: ${({ theme }) => theme.colors.error}20;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 24px;

    svg {
      color: ${({ theme }) => theme.colors.error};
    }
  }

  h2 {
    color: ${({ theme }) => theme.colors.text};
    margin-bottom: 16px;
  }

  p {
    color: ${({ theme }) => theme.colors.textSecondary};
    margin-bottom: 32px;
  }
`
